import { TAG_COLORS } from '../../dataSource/tagColors'
import { TITLE_FOREGROUND, nearestTitleColor, srgbToOklab } from '../../projectIcon/titlePalette'

/**
 * **Todoists Farben** (T08, #173) — Name → Hex, wie Todoist sie zeigt
 * (Datenkarte `tools/todoist-api-map/index.html`, `COLORS`).
 */
export const TODOIST_COLORS: Readonly<Record<string, string>> = {
  berry_red: '#B8255F', red: '#DC4C3E', orange: '#C77100', yellow: '#B29104',
  olive_green: '#949C31', lime_green: '#65A33A', green: '#369307', mint_green: '#42A393',
  teal: '#148FAD', sky_blue: '#319DC0', light_blue: '#6988A4', blue: '#4180FF',
  grape: '#692EC2', violet: '#CA3FEE', lavender: '#A4698C', magenta: '#E05095',
  salmon: '#C9766F', charcoal: '#808080', grey: '#999999', taupe: '#8F7A69',
}

/** Wie Todoist ein Projekt zeigt: ein `#` — hier das aus dem Icon-Werkzeug. */
export const TODOIST_PROJECT_ICON = 'lucide:Hash'

function rgbOf(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

export function todoistHex(name: string | null | undefined): string | null {
  return (name && TODOIST_COLORS[name]) ?? null
}

/**
 * Die ähnlichste Farbe aus dem Icon-Werkzeug. Grau bietet das Werkzeug nicht
 * an; Todoists Grautöne nehmen deshalb Schwarz, das Nächste dazu (Sven,
 * 27.09.2026). Unbekannter Name → `null` = Ton des Lebensbereichs.
 */
export function projectIconColor(name: string | null | undefined): string | null {
  const hex = todoistHex(name)
  if (!hex) return null
  const key = nearestTitleColor(rgbOf(hex))
  return key === 'gray' ? TITLE_FOREGROUND : key
}

function hueAndChroma(hex: string): { hue: number; chroma: number } {
  const [, a, b] = srgbToOklab(...rgbOf(hex))
  return { hue: ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360, chroma: Math.hypot(a, b) }
}

/** Unter dieser Buntheit ist eine Todoist-Farbe grau — Tags haben kein Grau. */
const TAG_NEUTRAL_CHROMA = 0.045

/**
 * Die Tag-Farbe mit dem nächsten Farbton — Tags haben nur diese acht. Nach
 * Farbton wie bei den Icons, nicht nach RGB-Abstand: Der machte aus Anthrazit
 * Grün. Grautöne → `null`, dann gilt die übliche Reihenfolge (`nextTagColor`).
 */
export function tagColor(name: string | null | undefined): string | null {
  const hex = todoistHex(name)
  if (!hex) return null
  const { hue, chroma } = hueAndChroma(hex)
  if (chroma < TAG_NEUTRAL_CHROMA) return null
  let best: string | null = null
  let bestDistance = Infinity
  for (const candidate of TAG_COLORS) {
    const other = hueAndChroma(candidate).hue
    const distance = Math.min(Math.abs(other - hue), 360 - Math.abs(other - hue))
    if (distance < bestDistance) {
      bestDistance = distance
      best = candidate
    }
  }
  return best
}
